import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubmissionPublicationAppService from '../../../../application/platform/sep/submission-publication/submission-publication-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { ISubmissionPublicationDetailResponse } from '../../../../model/sep/submission-publications/response/submission-publication-detail-response';
import { ISubmissionPublicationFileResponse } from '../../../../model/sep/submission-publications/response/submission-publication-file-response';
import { ISubmissionPublicationUpdateRequest } from '../../../../model/sep/submission-publications/request/submission-publication-update-request';
import { isEmptyId } from '../../../../utils/validation';

// Dados, edição (status/observação) e downloads da remessa aberta no drawer
export const useSubmissionPublicationDetailController = (submissionId: number | undefined, onSaved: () => void, onClose: () => void) => {
    const navigate = useNavigate();
    const { showSuccess, showError, confirmAction, withLoading } = useFeedback();
    const [detail, setDetail] = useState<ISubmissionPublicationDetailResponse>();
    const [files, setFiles] = useState<ISubmissionPublicationFileResponse[]>([]);
    const [form, setForm] = useState<ISubmissionPublicationUpdateRequest>({});
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const statusError = isEmptyId(form.statusId) ? 'Selecione o status' : undefined;

    useEffect(() => {
        setDetail(undefined);
        setFiles([]);
        setForm({});
        setShowValidationErrors(false);
        if (submissionId === undefined) return;
        loadDetail(submissionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionId]);

    async function loadDetail(id: number) {
        setLoading(true);
        try {
            const [data, submissionFiles] = await Promise.all([SubmissionPublicationAppService.getById(id), SubmissionPublicationAppService.listFiles(id)]);
            setDetail(data);
            setFiles(submissionFiles);
            setForm({ id: data.id, statusId: data.statusId, diagramerObservation: data.diagramerObservation });
        } catch (error: any) {
            showError('Não foi possível carregar a remessa', error);
        } finally {
            setLoading(false);
        }
    }

    function handleFormChange(changes: ISubmissionPublicationUpdateRequest) {
        setForm((previous) => ({ ...previous, ...changes }));
    }

    async function handleSave() {
        setShowValidationErrors(true);
        if (statusError) return;

        setIsSaving(true);
        try {
            await SubmissionPublicationAppService.update(form);
            showSuccess('Remessa atualizada com sucesso!');
            onSaved();
            onClose();
        } catch (error: any) {
            showError('Não foi possível salvar a remessa', error);
        } finally {
            setIsSaving(false);
        }
    }

    function download(work: () => Promise<{ download: () => void }>) {
        return withLoading(async () => {
            try {
                (await work()).download();
            } catch (error: any) {
                showError('Não foi possível baixar o arquivo', error);
            }
        });
    }

    function handleDownloadFile(file: ISubmissionPublicationFileResponse) {
        return download(() => SubmissionPublicationAppService.downloadFile(file.idRemessa, file.id));
    }

    function handleDownloadAll() {
        if (!detail?.id) return;
        const id = detail.id;
        return download(() => SubmissionPublicationAppService.downloadFiles(id));
    }

    function handlePublish() {
        confirmAction('Publicar remessa', 'Deseja publicar esta remessa no Diário Oficial?', () => navigate(`/diario-oficial/publicar/${submissionId}`));
    }

    return {
        detail,
        files,
        form,
        loading,
        isSaving,
        statusError: showValidationErrors ? statusError : undefined,
        handleFormChange,
        handleSave,
        handleDownloadFile,
        handleDownloadAll,
        handlePublish
    };
}
